import { BaseSyntheticEvent, FC, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { RichMediaInput, RichMediaState, useRichInput } from '@bonusly/common-action-input';
import { ErrorMessage } from '@bonusly/common-admin';
import { RichTextInput, useRichText } from '@bonusly/common-rich-text-editor';
import {
	Box,
	Button,
	Flex,
	FormControl,
	FormLabel,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	rem,
	Spacer,
	Stack,
	useDisclosure,
} from '@bonusly/design-system';
import { useAnalytics } from '@bonusly/who-am-i';

import { useFetchDirectReports } from '../../hooks/use-fetch-direct-reports';
import { useFetchDraftByRecipient } from '../../hooks/use-fetch-draft-by-recipient';
import { useFetchUsers } from '../../hooks/use-fetch-users';
import { User } from '../../lib';
import { ReferenceEntity } from '../../lib/entities/reference';
import { AmplitudeEvent } from '../../types/amplitude-event';
import { createContent } from '../../utils/content';
import { DeleteDraftFeedbackModal } from '../drafts/delete-draft-feedback-modal';
import { ReferencePreview } from '../reference';
import { useAudioTranscription } from './hooks/use-audio-transcription';
import { useSubmitUnrequestedFeedback } from './hooks/use-submit-unrequested-feedback';
import { TranscribeControls } from './transcribe-controls';
import { TranscriptionVisualizer, TranscriptionVisualizerRef } from './transcription-visualizer';
import { feedbackTextValidation, recipientsValidation } from './validations';

interface RequestFeedbackProps {
	defaultRecipients?: User[];
	evaluationId?: string;
	isOpen: boolean;
	onClose: VoidFunction;
	reference?: ReferenceEntity;
	text?: string;
}

export const GiveUnrequestedFeedbackModal: FC<RequestFeedbackProps> = ({ defaultRecipients, evaluationId, isOpen, onClose, reference, text }) => {
	const { t } = useTranslation('feedback');
	const { track } = useAnalytics();
	const visualizerRef = useRef<TranscriptionVisualizerRef>(null);

	useEffect(() => {
		// Ensure we only send an event when a user clicks to open the modal
		// and not on re-renders of the same modal being opened
		if (isOpen == true) {
			track(AmplitudeEvent.UnsolicitedFeedbackModalOpened);
		}
	}, [track, isOpen]);

	const editorDefaultRecipients = useMemo(() => {
		if (!defaultRecipients || !defaultRecipients.length) {
			return undefined;
		}

		return createContent(...defaultRecipients);
	}, [defaultRecipients]);

	// When fetching the drafts, fetch for only one user.
	const draftResponseForRecipient = useFetchDraftByRecipient({
		recipientId: defaultRecipients?.[0]?.id,
	});

	if (!evaluationId && defaultRecipients?.length === 1 && draftResponseForRecipient) {
		evaluationId = draftResponseForRecipient.id;
		reference = draftResponseForRecipient.reference;
		text = draftResponseForRecipient.lastDraft?.text;
	}

	const isEditing = !!evaluationId;
	const fetchRecipients = useFetchUsers();
	const { data } = useFetchDirectReports();
	const directReportIds = data?.map((user) => user.id) || [];

	const { editor: recipientEditor, uniqueId } = useRichInput({
		content: editorDefaultRecipients,
		modules: ['recipient'],
		fetchRecipients,
		placeholder: t('feedback.unrequested.recipients_placeholder'),
		options: {
			recipient: {
				matchWithoutTrigger: true,
			},
		},
	});

	const {
		editor: feedbackEditor,
		text: feedbackText,
		html: feedbackHtml,
	} = useRichText({
		content: text,
		placeholder: t('feedback.unrequested.feedback_placeholder'),
	});

	const transcribe = useAudioTranscription({
		onBuffer: (data) => visualizerRef.current?.draw(data),
		editor: feedbackEditor,
	});

	const onReset = () => {
		recipientEditor?.commands.clearContent();
		feedbackEditor?.commands.clearContent();
		transcribe.reset();
	};

	// Ensure when the modal is first opened that initial recipients are set
	useEffect(() => {
		if (editorDefaultRecipients && recipientEditor) {
			recipientEditor.commands.setContent(editorDefaultRecipients);
			recipientEditor.setEditable(!defaultRecipients || defaultRecipients.length === 0);
		}
	}, [editorDefaultRecipients, recipientEditor, defaultRecipients]);

	// Ensure when the modal opens the initial feedback text is set
	useEffect(() => {
		if (text) {
			feedbackEditor?.commands.setContent(text);
		}
	}, [editorDefaultRecipients, feedbackEditor, text]);

	// NOTE: let the user decide if they want to show the bonus or not,
	// default to showing it
	const [showBonusPreview, setShowBonusPreview] = useState(true);

	// NOTE: we want to hide the bonus preview the reference type is not a Bonus
	useEffect(() => {
		if (reference?.type === 'Bonus') {
			setShowBonusPreview(true);
		} else {
			setShowBonusPreview(false);
		}
	}, [reference]);

	const onRemoveBonus = () => {
		setShowBonusPreview(false);
	};

	const recipientData = RichMediaState.parse(recipientEditor);
	const recipients = recipientData.recipients;

	const { onSubmit: submitFeedback, isLoading: loadingSubmitFeedback } = useSubmitUnrequestedFeedback({
		feedbackText: feedbackHtml,
		evaluationId,
		onClose,
		onReset,
		recipientId: recipients[0]?.id,
		// NOTE: dont include the bonus reference if the user chose not to show it
		reference: reference?.type === 'Bonus' && !showBonusPreview ? undefined : reference,
	});

	const onSubmit = (event: BaseSyntheticEvent) => {
		event.preventDefault();
		track(AmplitudeEvent.UnsolicitedFeedbackSent);
		submitFeedback({ isDraft: false });
	};

	const onSaveAsDraft = (event: BaseSyntheticEvent) => {
		event.preventDefault();
		track(AmplitudeEvent.UnsolicitedFeedbackDraftSaved);
		submitFeedback({ isDraft: true });
	};

	const { hasMoreThanOneRecipient, recipientIsDirectReport, hasRecipients } = recipientsValidation(recipients, directReportIds);
	const { hasFeedbackText } = feedbackTextValidation(feedbackText);

	const canSubmit = recipientIsDirectReport && hasRecipients && !hasMoreThanOneRecipient && hasFeedbackText && !loadingSubmitFeedback && !transcribe.isActive;

	// Delete
	const deleteModal = useDisclosure();
	const handleDeleteClick = () => {
		deleteModal.onOpen();
	};

	return (
		<>
			<Modal autoFocus={false} isOpen={isOpen} onClose={onClose} onCloseComplete={onReset} scrollBehavior="inside" size="2xl">
				<ModalOverlay />
				<ModalContent as="form" onSubmit={onSubmit}>
					<ModalHeader borderBottomWidth={rem(1)} borderColor="gray.100" borderStyle="solid" color="gray.900">
						{t('feedback.response.give_feedback')}
					</ModalHeader>
					<ModalCloseButton size="lg" borderRadius="full" />

					<ModalBody px={5} pt={5} pb={0}>
						<Stack spacing={4}>
							<FormControl isInvalid={hasMoreThanOneRecipient || !recipientIsDirectReport}>
								<FormLabel>{t('feedback.unrequested.recipients_label')}</FormLabel>
								<RichMediaInput border="1px solid" borderColor="inherit" borderRadius="md" editor={recipientEditor} fontSize="lg" uniqueId={uniqueId} />
								{hasMoreThanOneRecipient && <ErrorMessage>{t('feedback.unrequested.errors.one_recipient')}</ErrorMessage>}
								{!recipientIsDirectReport && <ErrorMessage>{t('feedback.unrequested.errors.direct_report')}</ErrorMessage>}
							</FormControl>

							<FormControl>
								<FormLabel>{t('feedback.response.share_insights')}</FormLabel>

								{transcribe.isRecording ? (
									<TranscriptionVisualizer
										mode={transcribe.mode}
										onAccept={transcribe.sourceRephrased}
										onReject={transcribe.sourceRaw}
										onStop={transcribe.stop}
										ref={visualizerRef}
										state={transcribe.state}
									/>
								) : (
									<RichTextInput editor={feedbackEditor} fontSize="lg">
										<TranscribeControls editor={feedbackEditor} transcribe={transcribe} />
									</RichTextInput>
								)}
							</FormControl>

							{showBonusPreview && <ReferencePreview reference={reference} onRemove={onRemoveBonus} />}
						</Stack>
					</ModalBody>

					<ModalFooter>
						<Spacer />
						<Flex justify={isEditing ? 'space-between' : 'flex-end'} w="100%">
							{isEditing && (
								<Button colorScheme="secondary" variant="solid" alignSelf="start" onClick={handleDeleteClick}>
									{t('feedback.unrequested.delete_button')}
								</Button>
							)}
							<Box>
								<Button colorScheme="secondary" isDisabled={!canSubmit} mr={3} variant="alpha" onClick={onSaveAsDraft}>
									{t('feedback.response.save_as_draft')}
								</Button>

								<Button type="submit" colorScheme="primary" isDisabled={!canSubmit} variant="solid">
									{t('feedback.response.share_feedback')}
								</Button>
							</Box>
						</Flex>
					</ModalFooter>
				</ModalContent>
			</Modal>
			<DeleteDraftFeedbackModal onCloseGiveModal={onClose} feedbackId={evaluationId} isOpen={deleteModal.isOpen} onClose={deleteModal.onClose} />
		</>
	);
};
